import { MessageAppend } from '@bitte-ai/types';
import { memo } from 'react';
import { Button } from '../../../ui/button';
import { ChartWrapper } from '../../../ui/charts/ChartWrapper';
import { BitteSpinner } from '../../BitteSpinner';
import { CodeBlock } from '../../CodeBlock';
import { ErrorBoundary } from '../../ErrorBoundary';
import { EvmTxCard } from '../../transactions/EvmTxCard';
import { IntentsCard } from '../../transactions/IntentsCard';
import { ReviewTransaction } from '../../transactions/ReviewTransaction';
import { SuiTxCard } from '../../transactions/SuiTxCard';

import { formatAgentId } from '../../../../lib/chat';
import { BittePrimitiveName } from '../../../../lib/constants';
import { isDataString } from '../../../../lib/regex';
import { useChatState } from '../../../../state/chat.state';
import Swap from '../../../tools/swap/Swap';
import Transfer from '../../../tools/swap/Transfer';
import { ImprovedMessage } from '../../ImprovedMessage';

interface ToolCallResultProps {
  toolName: string;
  result: any;
  hideToolCall?: boolean;
  showBorder?: boolean;
  messageId: string;
  groupKey: string;
  chatId: string;
  creator?: string;
  borderColor?: string;
  messageBackgroundColor?: string;
  textColor?: string;
  customApproveTxButton?: React.ComponentType<any>;
  customDeclineTxButton?: React.ComponentType<any>;
  customTxContainer?: React.ComponentType<any>;
  append?: MessageAppend;
  customToolComponents?: any[];
}

const ToolCallResultComponent = ({
  toolName,
  result,
  hideToolCall,
  showBorder,
  messageId,
  groupKey,
  chatId,
  creator,
  borderColor,
  messageBackgroundColor,
  textColor,
  customApproveTxButton,
  customDeclineTxButton,
  customTxContainer,
  append,
  customToolComponents,
}: ToolCallResultProps) => {
  const chatState = useChatState();

  // Helper function to check if a transaction is completed by looking at system messages
  const isTransactionCompleted = (messageId: string): boolean => {
    const messages = chatState.messages;
    return messages.some((message) => {
      if (message.role === 'system' && message.content) {
        try {
          const systemMessage = JSON.parse(message.content);
          return (
            systemMessage.messageId === messageId &&
            (systemMessage.status === 'success' ||
              systemMessage.status === 'fail')
          );
        } catch {
          return false;
        }
      }
      return false;
    });
  };

  // Helper function to check if there's a completed EVM transaction for the same message
  const hasCompletedEvmTransaction = (messageId: string): boolean => {
    const messages = chatState.messages;

    // Find the message with the given messageId
    const currentMessage = messages.find((msg) => msg.id === messageId);
    if (!currentMessage || !currentMessage.parts) return false;

    // Check if there's a completed GENERATE_EVM_TX tool call in the same message
    return currentMessage.parts.some((part) => {
      if (part.type !== 'tool-invocation' || !part.toolInvocation) {
        return false;
      }

      const toolInvocation = part.toolInvocation;
      if (
        toolInvocation.toolName !== 'generate-evm-tx' ||
        toolInvocation.state !== 'result' ||
        !toolInvocation.result
      ) {
        return false;
      }

      const result = toolInvocation.result;
      if (!result.data || typeof result.data !== 'object' || !result.data) {
        return false;
      }

      const data = result.data as any;

      if (data.evmSignRequest && !data.ui) {
        return true;
      }

      return (
        data.ui &&
        data.evmSignRequest &&
        (data.ui.type === 'swap' || data.ui.type === 'transfer-ft')
      );
    });
  };

  if (!result || (result?.error && !hideToolCall)) {
    return <CodeBlock content={`Error: ${result?.error || 'Unknown Error'}`} />;
  }

  // Check for custom tool component first
  const customTool = customToolComponents?.find((tool) => {
    if ('toolName' in tool) return tool.toolName === toolName;
    if ('name' in tool) return tool.name === toolName;
    return false;
  });

  if (customTool && 'component' in customTool) {
    const CustomComponent = customTool.component;
    return <CustomComponent data={result.data || result} />;
  }

  switch (toolName) {
    case BittePrimitiveName.GENERATE_TRANSACTION: {
      const transactions = result?.data?.transactions || [];
      const warnings = result?.data?.warnings || [];

      if (!append) {
        return (
          <CodeBlock content="Append function is required for transaction handling" />
        );
      }

      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          {transactions.length > 0 ? (
            <div className="bitte-my-4">
              <ReviewTransaction
                chatId={chatId}
                creator={creator}
                transactions={transactions}
                warnings={warnings}
                agentId={formatAgentId('Bitte-Assistant')}
                borderColor={borderColor || ''}
                messageBackgroundColor={messageBackgroundColor || ''}
                textColor={textColor || ''}
                customApproveTxButton={customApproveTxButton}
                customDeclineTxButton={customDeclineTxButton}
                customTxContainer={customTxContainer}
                append={append}
                messageId={messageId}
              />
            </div>
          ) : (
            <div className="bitte-my-4">
              <div className="bitte-space-y-4">
                <div className="bitte-bg-secondary bitte-rounded-lg bitte-p-4">
                  <div className="bitte-flex bitte-items-center bitte-gap-4">
                    <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
                    <span className="bitte-text-secondary-foreground bitte-font-medium">
                      Generating transaction...
                    </span>
                  </div>
                </div>
                <div className="bitte-flex bitte-justify-center">
                  <BitteSpinner width={80} height={80} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </ErrorBoundary>
      );
    }

    case BittePrimitiveName.GENERATE_EVM_TX: {
      const payloadRes = result?.data?.ui;
      const evmSignRequest = result?.data?.evmSignRequest;

      if (!append) {
        return (
          <CodeBlock content="Append function is required for EVM transaction handling" />
        );
      }

      const renderEvmTxCard = (evmData: any) => {
        return (
          <EvmTxCard
            evmData={evmData}
            borderColor={borderColor || ''}
            messageBackgroundColor={messageBackgroundColor || ''}
            textColor={textColor || ''}
            customApproveTxButton={customApproveTxButton}
            customDeclineTxButton={customDeclineTxButton}
            customTxContainer={customTxContainer}
            append={append}
            messageId={messageId}
          />
        );
      };

      // Check for evmSignRequest first, outside other conditions
      if (evmSignRequest && !payloadRes) {
        return (
          <ErrorBoundary key={`${groupKey}-${messageId}`}>
            <div className="bitte-my-4 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-2 bitte-duration-300">
              {renderEvmTxCard(evmSignRequest)}
            </div>
          </ErrorBoundary>
        );
      }

      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          {evmSignRequest &&
          payloadRes &&
          (payloadRes.type === 'swap' ||
            payloadRes.type === 'transfer-ft' ||
            payloadRes.type === 'evm-tx') ? (
            <div className="bitte-my-4 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-2 bitte-duration-300">
              {payloadRes.type === 'swap' ? (
                <Swap data={payloadRes} tx={evmSignRequest} />
              ) : payloadRes.type === 'transfer-ft' ? (
                <Transfer data={payloadRes} tx={evmSignRequest} />
              ) : (
                renderEvmTxCard(evmSignRequest)
              )}
            </div>
          ) : payloadRes &&
            (payloadRes.type === 'swap' || payloadRes.type === 'transfer-ft') &&
            !evmSignRequest ? (
            // Show intermediate state when we have UI data but no transaction yet
            <div className="bitte-my-4 bitte-animate-in bitte-fade-in-0 bitte-slide-in-from-top-2 bitte-duration-300">
              <div className="bitte-space-y-4">
                <div className="bitte-bg-secondary/50 bitte-rounded-lg bitte-p-4 bitte-border-l-4 bitte-border-blue-500">
                  <div className="bitte-flex bitte-items-center bitte-gap-4">
                    <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-blue-500 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
                    <span className="bitte-text-secondary-foreground bitte-font-medium">
                      Quote received! Preparing transaction...
                    </span>
                  </div>
                </div>
                {payloadRes.type === 'swap' ? (
                  <div className="bitte-opacity-75 bitte-pointer-events-none">
                    <Swap data={payloadRes} tx={evmSignRequest} />
                  </div>
                ) : (
                  <div className="bitte-opacity-75 bitte-pointer-events-none">
                    <Transfer data={payloadRes} tx={evmSignRequest} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Default loading state
            <div className="bitte-my-4">
              <div className="bitte-space-y-4">
                <div className="bitte-bg-secondary bitte-rounded-lg bitte-p-4 bitte-animate-in bitte-fade-in-0 bitte-duration-300">
                  <div className="bitte-flex bitte-items-center bitte-gap-4">
                    <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
                    <span className="bitte-text-secondary-foreground bitte-font-medium">
                      Generating your transaction...
                    </span>
                  </div>
                </div>
                <div className="bitte-flex bitte-justify-center bitte-animate-in bitte-fade-in-0 bitte-duration-500">
                  <BitteSpinner width={80} height={80} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </ErrorBoundary>
      );
    }

    // Add support for intermediate swap tool results
    case 'swap': {
      const uiData = result?.data?.ui;

      if (!append) {
        return (
          <CodeBlock content="Append function is required for swap handling" />
        );
      }

      // Return null if transaction is completed (check system messages)
      if (isTransactionCompleted(messageId)) {
        return null;
      }

      // Return null if there's already a completed EVM transaction for this message
      if (hasCompletedEvmTransaction(messageId)) {
        return null;
      }

      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          {uiData && uiData.type === 'swap' ? (
            <div className="bitte-my-4">
              <div className="bitte-space-y-4">
                <div className="bitte-bg-secondary bitte-rounded-lg bitte-p-4 bitte-animate-in bitte-fade-in-0 bitte-duration-300">
                  <div className="bitte-flex bitte-items-center bitte-gap-4">
                    <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
                    <span className="bitte-text-secondary-foreground bitte-font-medium">
                      Finding the best swap route...
                    </span>
                  </div>
                </div>
                <div className="bitte-flex bitte-justify-center bitte-animate-in bitte-fade-in-0 bitte-duration-500">
                  <BitteSpinner width={80} height={80} color="#FFFFFF" />
                </div>
              </div>
            </div>
          ) : (
            <div className="bitte-my-4">
              <div className="bitte-space-y-4">
                <div className="bitte-bg-secondary bitte-rounded-lg bitte-p-4 bitte-animate-in bitte-fade-in-0 bitte-duration-300">
                  <div className="bitte-flex bitte-items-center bitte-gap-4">
                    <div className="bitte-w-4 bitte-h-4 bitte-border-2 bitte-border-gray-400 bitte-border-t-transparent bitte-rounded-full bitte-animate-spin" />
                    <span className="bitte-text-secondary-foreground bitte-font-medium">
                      Finding the best swap route...
                    </span>
                  </div>
                </div>
                <div className="bitte-flex bitte-justify-center bitte-animate-in bitte-fade-in-0 bitte-duration-500">
                  <BitteSpinner width={80} height={80} color="#FFFFFF" />
                </div>
              </div>
            </div>
          )}
        </ErrorBoundary>
      );
    }

    case BittePrimitiveName.GENERATE_IMAGE: {
      const url = result?.data?.url || '';
      return <img src={url} className="bitte-w-full" alt="Generated content" />;
    }

    case BittePrimitiveName.CREATE_DROP: {
      return (
        <div className="bitte-flex bitte-items-center bitte-justify-center bitte-gap-2">
          <Button asChild variant="link">
            <a href={`/claim/${result.data}`} target="_blank" rel="noreferrer">
              View Drop
            </a>
          </Button>
        </div>
      );
    }

    case BittePrimitiveName.GENERATE_SUI_TX: {
      const suiTransactionBytes = result?.data?.suiTransactionBytes || '';

      if (!append) {
        return (
          <CodeBlock content="Append function is required for SUI transaction handling" />
        );
      }

      if (suiTransactionBytes) {
        return (
          <SuiTxCard
            suiTransactionBytes={suiTransactionBytes}
            borderColor={borderColor || ''}
            messageBackgroundColor={messageBackgroundColor || ''}
            textColor={textColor || ''}
            customApproveTxButton={customApproveTxButton}
            customDeclineTxButton={customDeclineTxButton}
            customTxContainer={customTxContainer}
            append={append}
            messageId={messageId}
          />
        );
      }
      break;
    }

    case BittePrimitiveName.RENDER_CHART: {
      if (typeof result.data === 'object' && result.data) {
        const chartData = result.data;
        return (
          <ChartWrapper
            title={chartData.title || ''}
            description={chartData.description || ''}
            chartData={chartData.chartData}
            chartConfig={chartData.chartConfig}
            dataFormat={chartData.dataFormat}
            chartType={chartData.chartType}
            metricLabels={chartData.metricLabels || []}
            metricData={chartData.metricData || []}
          />
        );
      }
      break;
    }

    case 'intents': {
      return (
        <ErrorBoundary key={`${groupKey}-${messageId}`}>
          <div className="bitte-my-4">
            <IntentsCard
              data={result.data || result}
              borderColor={borderColor || ''}
              messageBackgroundColor={messageBackgroundColor || ''}
              textColor={textColor || ''}
              customApproveTxButton={customApproveTxButton}
              customDeclineTxButton={customDeclineTxButton}
              customTxContainer={customTxContainer}
              append={append!}
              messageId={messageId}
            />
          </div>
        </ErrorBoundary>
      );
    }

    default: {
      if (showBorder) {
        return null;
      }

      // Filter out verbose JSON payloads for transaction-related tools
      const isTransactionTool =
        toolName.toLowerCase().includes('swap') ||
        toolName.toLowerCase().includes('transaction') ||
        toolName.toLowerCase().includes('evm') ||
        toolName.toLowerCase().includes('cowswap') ||
        toolName.toLowerCase().includes('intents');
      toolName.toLowerCase().includes('generate-evm-tx');

      if (isTransactionTool) {
        // For transaction tools, don't show the raw JSON payload
        return null;
      }

      const data = result?.data || result;
      const stringifiedData =
        typeof data === 'string' ? data : JSON.stringify(data);

      // Check if this looks like markdown content (contains headers or structured content)
      const hasMarkdownHeaders = /^#{1,6}\s+/m.test(stringifiedData);
      const hasStructuredContent =
        /(Network|From|To|Sell|Buy \(est\.\)|Fee \(included\)):\s*/.test(
          stringifiedData,
        );

      if (hasMarkdownHeaders || hasStructuredContent) {
        return <ImprovedMessage content={stringifiedData} />;
      }

      return isDataString(stringifiedData) ? (
        <CodeBlock content={stringifiedData} />
      ) : (
        <p>{stringifiedData}</p>
      );
    }
  }

  return null;
};

export const ToolCallResult = memo(
  ToolCallResultComponent,
  (prevProps, nextProps) => {
    // Simplified comparison - only check essential props
    if (prevProps.toolName !== nextProps.toolName) return false;
    if (prevProps.result !== nextProps.result) return false;
    if (prevProps.hideToolCall !== nextProps.hideToolCall) return false;
    if (prevProps.messageId !== nextProps.messageId) return false;

    return true;
  },
);

ToolCallResult.displayName = 'ToolCallResult';
